import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Plus, Pin, ShieldCheck, BookOpen, ArrowLeft, ArrowRight, Search, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API_BASE_URL } from "@/config/api";
import { useAuth } from "@/config/auth";

const API = `${API_BASE_URL}/documents`;

export default function DocumentsList() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", category: "general", visibility: "users" });

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      setDocs(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load documents");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = docs.filter((d) =>
    !q ||
    d.title.toLowerCase().includes(q.toLowerCase()) ||
    d.category.toLowerCase().includes(q.toLowerCase()) ||
    d.content.toLowerCase().includes(q.toLowerCase())
  );

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(API, { ...form, content: "" });
      toast.success("Document created");
      setCreateOpen(false);
      setForm({ title: "", category: "general", visibility: "users" });
      navigate(`/documents/${res.data.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally { setCreating(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${doc.id}`);
      toast.success("Document deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="documents-list">
      <button onClick={() => navigate(-1)} className="text-xs text-[#617364] hover:text-[#43A047] flex items-center gap-1 mb-4" data-testid="docs-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-xl bg-[#43A047]/12 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#43A047]" />
            </div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>Documents</h1>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold tracking-wider bg-[#1B291D] text-white"><ShieldCheck className="w-2.5 h-2.5" /> ADMIN</span>
            )}
          </div>
          <p className="text-sm text-[#617364]">Centralized knowledge base for TOSS methodology, parameters, and supporting docs.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="docs-new">
            <Plus className="w-4 h-4 mr-1.5" /> New Document
          </Button>
        )}
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title, category or content..."
          className="pl-9 border-[#E0E6E1] bg-white"
          data-testid="docs-search"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#43A047]" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-[#E0E6E1] bg-white" data-testid="docs-empty">
          <FileText className="w-10 h-10 text-[#617364] mx-auto mb-2 opacity-50" />
          <p className="text-sm text-[#1B291D] font-medium">No documents yet.</p>
          <p className="text-xs text-[#617364] mt-1">
            {isAdmin ? "Click \"New Document\" to add one." : "Documents added by admins will appear here."}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((d) => (
            <Card
              key={d.id}
              onClick={() => navigate(`/documents/${d.slug}`)}
              className="p-4 sm:p-5 border-[#E0E6E1] bg-white hover:border-[#43A047]/50 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
              data-testid={`doc-card-${d.slug}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    {d.is_pinned && <Pin className="w-3.5 h-3.5 text-[#43A047]" />}
                    <h3 className="text-base font-semibold text-[#1B291D] tracking-tight truncate" style={{ fontFamily: 'Work Sans' }}>{d.title}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <Badge variant="outline" className="border-[#43A047]/30 text-[#388E3C] text-[10px] uppercase tracking-wider font-bold px-1.5 py-0">{d.category}</Badge>
                    {d.visibility === "admin" && (
                      <Badge variant="outline" className="border-[#7B1FA2]/30 text-[#7B1FA2] text-[10px] uppercase tracking-wider font-bold px-1.5 py-0">Admin-only</Badge>
                    )}
                  </div>
                  <p className="text-xs text-[#617364] leading-relaxed line-clamp-2">
                    {(d.content || "")
                      .replace(/[#*`>_~|\[\]()]/g, " ")
                      .replace(/\s+/g, " ")
                      .trim()
                      .slice(0, 160) || "No content yet."}
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-[10px] text-[#617364] tracking-wide">
                    <span>Updated {new Date(d.updated_at).toLocaleDateString()}</span>
                    {d.updated_by && <span>· by {d.updated_by}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <ArrowRight className="w-4 h-4 text-[#43A047] opacity-0 group-hover:opacity-100 transition-opacity" />
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/documents/${d.slug}?edit=1`); }}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-[#617364] hover:bg-[#43A047]/10 hover:text-[#43A047]"
                        title="Edit"
                        data-testid={`doc-edit-${d.slug}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {d.slug !== "process-documentation" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(d); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-[#617364] hover:bg-[#D32F2F]/10 hover:text-[#D32F2F]"
                          title="Delete"
                          data-testid={`doc-delete-${d.slug}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>New Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-[#E0E6E1]" data-testid="docs-new-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 border-[#E0E6E1]" data-testid="docs-new-category" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Visibility</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger className="mt-1 border-[#E0E6E1]" data-testid="docs-new-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="users">All logged-in users</SelectItem>
                    <SelectItem value="admin">Admins only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-[#E0E6E1]">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="docs-new-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create &amp; edit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
